import type { MeResponse } from '@signbento/contracts'
import type { LocalVault } from '@signbento/storage'

/**
 * The app shell's own states (PRD §9.3 "never blank the whole dashboard", PLT-10): why a page
 * could not render, and the one-time hint on a device that starts empty for a returning account.
 * Pure where it can be, so the rules are tested without a browser.
 */

/**
 * Pages that are not the sender's app: a recipient's signing link, a CC reader's final copy and
 * the installed apps' framed challenge. They have no Home of their own, and they don't install the
 * offline shell, so a recipient who opens one link doesn't download the whole app in the
 * background.
 */
export function isOutsideShell(pathname: string): boolean {
  return /^\/(sign|cc)\/[^/]/.test(pathname) || /^\/native-challenge\/?$/.test(pathname)
}

/** Why a page failed to render, in the terms the error screen explains. */
export type RouteFailure = 'update' | 'offline' | 'crash'

/**
 * A page's code that failed to download: after a deploy the old file name is gone, and offline a
 * page that was never opened is not on the device yet. Browsers word it differently.
 */
export function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false
  if (error.name === 'ChunkLoadError') return true
  return /dynamically imported module|importing a module script failed|unable to preload css|failed to load module script/i.test(
    error.message,
  )
}

/**
 * A missing page while offline says to connect; while online it means a new version replaced the
 * files this tab was built with, so a reload is the fix. Anything else is an unexpected error.
 */
export function routeFailureFor(error: unknown, online: boolean): RouteFailure {
  if (!isChunkLoadError(error)) return 'crash'
  return online ? 'update' : 'offline'
}

/** The vault setting that remembers the new-device hint: shown until dismissed. */
export const NEW_DEVICE_HINT_SETTING = 'shell.new_device_hint'

export type NewDeviceHintState = 'show' | 'dismissed'

/**
 * An account that has done something before (a request sent, the free trial used, Lifetime
 * bought) and so has documents somewhere. A first-ever account has none, and gets no hint.
 */
export function hasPriorActivity(
  me: Pick<MeResponse, 'plan' | 'trial' | 'active_request_count'>,
): boolean {
  return me.plan !== 'FREE' || me.trial !== 'AVAILABLE' || me.active_request_count > 0
}

/**
 * Set by sign-in before the account's vault opens, read once by the shell: the account signed in
 * on this device just now and has activity elsewhere. In memory on purpose — the shell takes over
 * from the sign-in screen as soon as the vault opens, and only this page load can know.
 */
let returningAccountSignedIn = false

export function noteSignIn(me: Pick<MeResponse, 'plan' | 'trial' | 'active_request_count'>): void {
  returningAccountSignedIn = hasPriorActivity(me)
}

/** One decision in flight per vault: a page that asks twice at once writes the setting once. */
const deciding = new WeakMap<LocalVault, Promise<boolean>>()

/**
 * Whether Home shows "your documents stay on the other device" for this vault. A returning
 * account whose vault here is empty sees it once it signs in, and keeps seeing it until it is
 * dismissed or the first document or template arrives (a restored backup, say).
 */
export function newDeviceHintVisible(vault: LocalVault): Promise<boolean> {
  const pending = deciding.get(vault)
  if (pending !== undefined) return pending
  const decision = decideNewDeviceHint(vault).finally(() => deciding.delete(vault))
  deciding.set(vault, decision)
  return decision
}

async function decideNewDeviceHint(vault: LocalVault): Promise<boolean> {
  const stored = await vault.getSetting<NewDeviceHintState>(NEW_DEVICE_HINT_SETTING)
  if (stored === 'dismissed') return false
  if (stored !== 'show' && !returningAccountSignedIn) return false
  const [documents, templates] = await Promise.all([
    vault.countDocuments(),
    vault.listTemplates().then((list) => list.length),
  ])
  returningAccountSignedIn = false
  if (documents > 0 || templates > 0) {
    if (stored === 'show') await vault.putSetting(NEW_DEVICE_HINT_SETTING, 'dismissed')
    return false
  }
  if (stored !== 'show') await vault.putSetting(NEW_DEVICE_HINT_SETTING, 'show')
  return true
}

export async function dismissNewDeviceHint(vault: LocalVault): Promise<void> {
  await vault.putSetting(NEW_DEVICE_HINT_SETTING, 'dismissed')
}

/** Test seam: the in-memory sign-in note is per page load. */
export function resetSignInNoteForTests(): void {
  returningAccountSignedIn = false
}
